import React, { useEffect } from 'react';
import {
  ArrowRight,
  Building2,
  CheckCircle2,
  Download,
  GraduationCap,
  History,
  Landmark,
  LockKeyhole,
  QrCode,
  ReceiptText,
  ScanLine,
  ShieldCheck,
  Smartphone,
  Store,
  UserRound,
  UsersRound,
  WalletCards,
} from 'lucide-react';
import PageMeta from '../components/PageMeta';
import {
  WONYAPAY_ANDROID_STORE_URL,
  WONYAPAY_IOS_STORE_URL,
  getWonyapaySmartDownloadUrl,
} from '../constants/links';
import { useLanguage } from '../i18n/LanguageProvider';
import appImage from '../assets/3.webp';

const DownloadApp = () => {
  const { t } = useLanguage();
  const smartDownloadUrl = getWonyapaySmartDownloadUrl();

  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  const features = [
    { icon: WalletCards, title: 'Paiements Mobile Money', text: 'Utilisez les services Mobile Money disponibles dans un parcours unifié.' },
    { icon: Smartphone, title: 'Encaissement mobile', text: 'Recevez et suivez les paiements directement depuis votre smartphone.' },
    { icon: QrCode, title: 'QR Code', text: 'Payez ou identifiez une opération rapidement grâce au scan.' },
    { icon: History, title: 'Historique et suivi', text: 'Consultez les transactions, leur statut et les reçus de paiement.' },
  ];

  const audiences = [
    { icon: UserRound, label: 'Particuliers' },
    { icon: Store, label: 'Marchands' },
    { icon: GraduationCap, label: 'Écoles et universités' },
    { icon: Building2, label: 'Entreprises' },
    { icon: Landmark, label: 'Institutions' },
    { icon: ShieldCheck, label: 'Services publics' },
    { icon: UsersRound, label: 'Organisations et projets' },
  ];

  return (
    <div className="ws-page ws-app" data-no-i18n="true">
      <PageMeta
        title="Application mobile WonyaPay | WonyaSoft"
        description="Payez, encaissez, scannez des QR Codes et suivez vos opérations avec l’application mobile WonyaPay."
      />

      <section className="ws-app-hero">
        <div className="ws-shell ws-app-hero__grid">
          <div className="ws-app-hero__copy">
            <span className="ws-eyebrow"><Smartphone size={18} aria-hidden="true" /> {t('Application de paiement mobile')}</span>
            <h1>{t('Vos paiements dans une seule application')}</h1>
            <p>{t('Avec l’application mobile WonyaPay, payez, encaissez et suivez vos opérations simplement depuis votre smartphone.')}</p>
            <div className="ws-button-row">
              <a className="ws-button ws-button--secondary" href={smartDownloadUrl}>
                <Download size={18} aria-hidden="true" />
                {t('Télécharger l’application mobile')}
              </a>
            </div>
            <div className="ws-app-hero__proofs">
              <span><CheckCircle2 size={18} aria-hidden="true" /> {t('Paiements Mobile Money')}</span>
              <span><ScanLine size={18} aria-hidden="true" /> {t('QR Code')}</span>
              <span><ReceiptText size={18} aria-hidden="true" /> {t('Historique et suivi')}</span>
            </div>
          </div>
          <div className="ws-app-hero__image">
            <div className="ws-app-hero__image-frame">
              <img src={appImage} alt={t('Utilisatrice de l’application mobile WonyaPay')} fetchPriority="high" />
            </div>
            <div className="ws-app-hero__floating-card">
              <LockKeyhole size={20} aria-hidden="true" />
              <span>{t('Des opérations simples et sécurisées')}</span>
            </div>
          </div>
        </div>
      </section>

      <section className="ws-section" aria-labelledby="ws-app-features-title">
        <div className="ws-shell">
          <div className="ws-section-heading ws-section-heading--center">
            <span className="ws-kicker">WonyaPay</span>
            <h2 id="ws-app-features-title">{t('Une expérience mobile pensée pour le quotidien')}</h2>
            <p>{t('WonyaPay réunit les opérations essentielles dans une interface simple, adaptée aux particuliers comme aux organisations.')}</p>
          </div>
          <div className="ws-feature-grid">
            {features.map(({ icon: Icon, title, text }) => (
              <article className="ws-feature-card" key={title}>
                <Icon size={25} aria-hidden="true" />
                <h3>{t(title)}</h3>
                <p>{t(text)}</p>
              </article>
            ))}
          </div>
        </div>
      </section>

      <section className="ws-section ws-section--soft" aria-labelledby="ws-app-audience-title">
        <div className="ws-shell">
          <div className="ws-section-heading">
            <span className="ws-kicker">WonyaPay</span>
            <h2 id="ws-app-audience-title">{t('Pour qui est conçue WonyaPay ?')}</h2>
          </div>
          <div className="ws-app-audiences">
            {audiences.map(({ icon: Icon, label }) => (
              <div key={label}><Icon size={21} aria-hidden="true" /><span>{t(label)}</span></div>
            ))}
          </div>
        </div>
      </section>

      <section className="ws-section">
        <div className="ws-shell ws-app-security">
          <div className="ws-app-security__icon"><ShieldCheck size={42} aria-hidden="true" /></div>
          <div>
            <span className="ws-kicker">{t('Sécurité')}</span>
            <h2>{t('Des opérations simples et sécurisées')}</h2>
            <p>{t('La sécurité, la traçabilité et la clarté des statuts sont intégrées au parcours afin que chaque utilisateur garde une lecture précise de ses opérations.')}</p>
          </div>
        </div>
      </section>

      <section className="ws-section" id="download">
        <div className="ws-shell ws-app-download">
          <div>
            <span className="ws-kicker">WonyaPay</span>
            <h2>{t('Accéder à WonyaPay')}</h2>
            <p>{t('Téléchargez WonyaPay sur votre smartphone.')}</p>
          </div>
          <div className="ws-button-row">
            <a className="ws-button ws-button--primary" href={WONYAPAY_ANDROID_STORE_URL} target="_blank" rel="noopener noreferrer">{t('Télécharger pour Android')} <ArrowRight size={18} aria-hidden="true" /></a>
            <a className="ws-button ws-button--secondary" href={WONYAPAY_IOS_STORE_URL} target="_blank" rel="noopener noreferrer">{t('Télécharger pour iPhone')}</a>
          </div>
        </div>
      </section>

    </div>
  );
};

export default DownloadApp;
